// =============================================================================
// ui/state/useLeakRules.ts — the only place a rule is written.
// -----------------------------------------------------------------------------
// `domain/leakLoop.ts` decides what the flags blob should become. This decides
// whether it is safe to save one at all, and reports what happened.
//
// ── 🚨 THE GATE THIS HOOK EXISTS FOR ──
//
// `usePreferences` keeps `update()` callable while a read has FAILED. That is
// correct for every other setting, because a patch carries only the field being
// changed and cannot revert what it never saw.
//
// It is not correct here. Rules live inside `app_flags`, which is one JSONB blob
// shared with the layout choices, the R:R unit, the tour flag and whatever the
// old client adds next — and a patch of that column carries the WHOLE thing. So
// after a failed read, `prefs` is null, the blob we would spread is `{}`, and
// saving a rule would wipe every other flag the trader has.
//
// `ready` is therefore checked before every write, and a refusal says so in
// words rather than failing quietly.
//
// ── ⚠ THE OPTIMISTIC UPDATE IS NOT ROLLED BACK ──
//
// `usePreferences.update()` applies the patch locally first and deliberately
// leaves it applied when the save fails, so an input does not jump under the
// user's cursor. That is right for a text field and misleading here: a new rule
// would appear in Fix it, with a baseline, looking saved.
//
// So every mutation returns its error AND this hook surfaces `saveError` from
// the provider. The screen has to say the rule did not stick; there is no way to
// make the list alone tell the truth.
// =============================================================================

import { useCallback, useMemo, useState } from 'react';
import { useSupabase } from './supabase';
import { usePreferences } from './preferences';
import {
  activeLeakRules, bankRule, createRule, leakLedger, retireRule,
  type LeakBaseline, type LeakRule, type LedgerRule,
} from '../../selectors/leaks';
import { saveAdherenceMark } from '../../io/sync/saveAdherenceMark';
import { reportFailure } from '../../io/diagnostics/errorLog';

export interface NewRuleInput {
  leakId: string;
  ruleText: string;
  baseline: LeakBaseline | null;
  /** ISO date the rule starts. Passed in — the clock is the caller's. */
  today: string;
}

export interface LeakRulesState {
  /** The rules currently being tested, oldest first. */
  rules: LeakRule[];
  /** Everything banked or retired, newest first. */
  ledger: LedgerRule[];
  /** False while preferences are loading or unreadable — no write may proceed. */
  canWrite: boolean;
  /** True while a save is in flight. */
  busy: boolean;
  /** The last failure, from either the flags write or a mark. */
  error: string | null;
  create: (input: NewRuleInput) => Promise<string | null>;
  bank: (ruleId: string, today: string) => Promise<string | null>;
  retire: (ruleId: string, reason: string, today: string) => Promise<string | null>;
  /** Mark one trade against one rule. `null` clears the judgement. */
  mark: (
    tradeId: string, ruleId: string, followed: boolean | null,
  ) => Promise<string | null>;
}

/** `crypto.randomUUID` is in every browser this client supports. */
const newRuleId = (): string => crypto.randomUUID();

export function useLeakRules(): LeakRulesState {
  const { sb, userId } = useSupabase();
  const { phase, prefs, update, saveError } = usePreferences();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = phase === 'ready' && prefs !== null;

  const rules = useMemo(() => activeLeakRules(prefs?.appFlags), [prefs]);
  const ledger = useMemo(() => leakLedger(prefs?.appFlags), [prefs]);

  const NOT_READY = 'Your settings haven’t loaded, so nothing can be saved yet — '
    + 'saving now would overwrite the ones this screen never read. Reload and try again.';

  /**
   * Save a whole flags blob.
   *
   * ⚠ Reads `prefs.appFlags` at CALL time rather than closing over a snapshot.
   * Two rules created without a reload in between would otherwise both spread
   * the same starting blob, and the second would erase the first.
   */
  const writeFlags = useCallback(
    async (build: (flags: Record<string, unknown>) => {
      appFlags: Record<string, unknown> | null; error: string | null;
    }): Promise<string | null> => {
      if (!canWrite || !prefs) { setError(NOT_READY); return NOT_READY; }
      const out = build(prefs.appFlags ?? {});
      if (!out.appFlags) { setError(out.error); return out.error; }
      setBusy(true);
      setError(null);
      try {
        // 🔴 THE RETURN VALUE, NOT THE ABSENCE OF A THROW (TCR-86). `update`
        // catches its own failure and records it in `saveError`, so this
        // `try` never fired on a rejected write and every caller was told the
        // rule had saved. The Fix-it dialog closed on that and threw away what
        // the trader had typed.
        const failed = await update({ appFlags: out.appFlags });
        if (failed) { setError(failed); return failed; }
        return null;
      } catch (e) {
        const msg = reportFailure('save leak rule', e, 'The rule could not be saved.');
        setError(msg);
        return msg;
      } finally {
        setBusy(false);
      }
    },
    [canWrite, prefs, update, NOT_READY],
  );

  const create = useCallback(
    (input: NewRuleInput) => writeFlags((flags) => createRule(flags, {
      id: newRuleId(),
      leakId: input.leakId,
      ruleText: input.ruleText,
      createdAt: input.today,
      baseline: input.baseline,
    })),
    [writeFlags],
  );

  const bank = useCallback(
    (ruleId: string, today: string) => writeFlags((flags) => bankRule(flags, ruleId, today)),
    [writeFlags],
  );

  const retire = useCallback(
    (ruleId: string, reason: string, today: string) =>
      writeFlags((flags) => retireRule(flags, ruleId, reason, today)),
    [writeFlags],
  );

  const mark = useCallback(
    async (tradeId: string, ruleId: string, followed: boolean | null) => {
      setBusy(true);
      setError(null);
      try {
        const r = await saveAdherenceMark(sb, tradeId, ruleId, followed, userId);
        if (r.error) setError(r.error);
        return r.error;
      } finally {
        setBusy(false);
      }
    },
    [sb, userId],
  );

  return useMemo(
    () => ({
      rules, ledger, canWrite, busy,
      // ⚠ The provider's own save error counts too. A rule can fail to persist
      // through `update()` while this hook's own call resolved cleanly, and the
      // optimistic list would show it as live either way.
      error: error ?? saveError,
      create, bank, retire, mark,
    }),
    [rules, ledger, canWrite, busy, error, saveError, create, bank, retire, mark],
  );
}
